/**
 * Header patient search — matching, BY NAME ONLY.
 *
 * Replaces vue-multiselect's built-in filter for the header search, which was a substring match
 * against "FName LName" and nothing else. Staff type the way the chart reads, so:
 *
 *   - Name words match by PREFIX in any order ("jo smith", "Smith, John"), accent- and
 *     apostrophe-insensitive. A plain substring still matches ("ith" finds Smith), ranked below
 *     every prefix match so "smith" lists the Smiths before Goldsmith.
 *   - Ranking: a token that EQUALS a whole name word (0) beats a word prefix (1) beats a substring
 *     (2), so "john" lists the Johns before Johnson and Johnston.
 *   - Every token must match. Results sort by rank, then last name, then first name.
 *   - searchPatientsWithCount also returns the TOTAL hit count (before the cap), so a caller can
 *     say "+N more" instead of silently dropping rows.
 *
 * ⛔ NAMES ONLY (Nilay, 2026-09-13). Birthdate and phone-number matching shipped briefly on the
 * feature branch and were removed before release: staff search by name, and the extra paths were
 * impractical in real use (a phone number typed as 845-702-6723 found nobody). A query containing
 * digits therefore matches no one and shows "No patient matches". Do not re-add birthdate or phone
 * matching without asking.
 *
 * The birthdate is still DISPLAYED under each result (formatDob) so two patients with the same name
 * can be told apart. ⛔ NEVER `new Date(Birthdate)`: /runquery returns DATE columns as bare
 * 'YYYY-MM-DD' strings, which parse as UTC midnight and read back as the PREVIOUS day in any US
 * browser — the Conversations DOB bug (conversations-patient-dob-label.test.js). It is sliced.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE. cypress/node-tests/patient-search-matching.test.js loads and
 * executes it directly (the phiMask.js pattern), so an import breaks the deploy gate.
 *
 * DEMO MASK: this runs over the roster the axios interceptor already masked, so it matches exactly
 * the (fake) names on screen.
 */

export const PATIENT_SEARCH_LIMIT = 20;

// Open Dental stores "no birthdate" as 0001-01-01.
const EARLIEST_REAL_BIRTH_YEAR = 1800;

function fold(value) {
  if (value == null) return '';
  let s = String(value);
  try {
    s = s.normalize('NFD');
  } catch (e) {
    // An engine without String.prototype.normalize keeps its accents; matching still works
    // for anyone who types the name the way it is stored.
  }
  // Drop the combining marks NFD split off ("José" → "jose"): the Combining Diacritical Marks
  // block, U+0300–U+036F. A char-code range rather than a regex so no invisible character or
  // escape sequence has to survive in this file.
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code < 0x0300 || code > 0x036f) out += s[i];
  }
  return out.toLowerCase();
}

// "Mary-Jane" → ['mary', 'jane'], "O'Brien" → ['obrien'], "Smith, John" → ['smith', 'john'].
function nameWords(value) {
  return fold(value).replace(/['’`.]/g, '').split(/[^a-z0-9]+/).filter(Boolean);
}

function dobParts(birthdate) {
  if (birthdate == null) return null;
  const m = String(birthdate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m || Number(m[1]) < EARLIEST_REAL_BIRTH_YEAR) return null;
  return { y: m[1], m: m[2], d: m[3] };
}

/**
 * A birthdate for DISPLAY in the site's DateFormat ('MM/DD/YYYY' or 'DD/MM/YYYY'), or '' when
 * there is none. Not used for matching.
 */
export function formatDob(birthdate, dateFormat) {
  const parts = dobParts(birthdate);
  if (!parts) return '';
  const fmt = typeof dateFormat === 'string' && dateFormat.indexOf('YYYY') > -1 ? dateFormat : 'MM/DD/YYYY';
  return fmt.replace(/YYYY|MM|DD/g, (t) => {
    if (t === 'YYYY') return parts.y;
    return t === 'MM' ? parts.m : parts.d;
  });
}

/**
 * Precompute the name words a keystroke needs. Build once per roster (a computed), not per
 * search — the roster can be tens of thousands of patients. `dateFormat` is accepted for API
 * stability; matching no longer reads it.
 */
export function buildPatientSearchIndex(patients) {
  if (!Array.isArray(patients)) return [];

  const index = [];
  for (let i = 0; i < patients.length; i++) {
    const patient = patients[i];
    if (!patient || typeof patient !== 'object') continue;

    const firstWords = nameWords(patient.FName);
    const lastWords = nameWords(patient.LName);
    const words = firstWords.concat(lastWords);
    // "Mary-Jane" is also typed "maryjane", "Van Der Berg" as "vanderberg".
    if (firstWords.length > 1) words.push(firstWords.join(''));
    if (lastWords.length > 1) words.push(lastWords.join(''));

    index.push({
      patient,
      order: i,
      words,
      haystack: firstWords.concat(lastWords).join(' '),
      firstKey: firstWords.join(' '),
      lastKey: lastWords.join(' '),
    });
  }
  return index;
}

// 0 = the token equals a whole name word, 1 = word-prefix match, 2 = substring match, -1 = no match.
function matchWord(entry, token) {
  let prefix = false;
  for (let i = 0; i < entry.words.length; i++) {
    const word = entry.words[i];
    if (word === token) return 0;
    if (word.indexOf(token) === 0) prefix = true;
  }
  if (prefix) return 1;
  return entry.haystack.indexOf(token) > -1 ? 2 : -1;
}

function compareText(a, b) {
  if (a < b) return -1;
  return a > b ? 1 : 0;
}

/**
 * Patients (the ORIGINAL roster objects) whose name matches `query`, best first, at most `limit`,
 * plus `total` — how many patients match before the cap, so the UI can say "+N more".
 * A blank query returns { patients: [], total: 0 } — the empty state belongs to Recently viewed.
 */
export function searchPatientsWithCount(index, query, limit) {
  const none = { patients: [], total: 0 };
  if (!Array.isArray(index) || index.length === 0 || query == null) return none;
  const tokens = nameWords(query);
  if (tokens.length === 0) return none;
  const max = Number.isInteger(limit) && limit > 0 ? limit : PATIENT_SEARCH_LIMIT;

  const hits = [];
  for (let i = 0; i < index.length; i++) {
    const entry = index[i];
    let rank = 0;
    let matched = true;
    for (let t = 0; t < tokens.length; t++) {
      const r = matchWord(entry, tokens[t]);
      if (r < 0) {
        matched = false;
        break;
      }
      if (r > rank) rank = r;
    }
    if (matched) hits.push({ entry, rank });
  }

  hits.sort((a, b) => (a.rank - b.rank)
    || compareText(a.entry.lastKey, b.entry.lastKey)
    || compareText(a.entry.firstKey, b.entry.firstKey)
    || (a.entry.order - b.entry.order));

  return { patients: hits.slice(0, max).map((h) => h.entry.patient), total: hits.length };
}

/**
 * Patients (the ORIGINAL roster objects) whose name matches `query`, best first, at most `limit`.
 * A blank query returns [] — the empty state belongs to Recently viewed.
 */
export function searchPatients(index, query, limit) {
  return searchPatientsWithCount(index, query, limit).patients;
}
